/**
 * MergeFieldPicker — per-field comparison row inside MergeReview.
 *
 * Renders the field name + winner/loser values side-by-side with
 * three radio choices (keep / replace / custom). Fields whose status
 * is 'agree' render as a compact "✓ matches" row with no operator
 * action needed (still tracked in field_decisions for forensic
 * completeness).
 *
 * Phase 1.14.3.1 brand polish — sage palette, Inter body, softer
 * microcopy. Null indicator renders as "(empty)" instead of the em
 * dash per brand guidelines (no em dashes anywhere).
 *
 * Read-only when `disabled` (parent passes true after status
 * transitions to committed | cancelled).
 *
 * Phase 1.14.3: ships with the merge primitive. The shape extends to
 * individuals + clients via the same picker component once entity-
 * class-specific MergeDomainField lists are added.
 */

import { type ReactNode } from 'react';
import { Check } from 'lucide-react';
import type { MergeDraftPlanField, MergeFieldDecision } from '@/lib/types';

export interface MergeFieldPickerProps {
  field: MergeDraftPlanField;
  decision: MergeFieldDecision;
  /** Operator changes the field decision; parent re-renders with new state. */
  onChange: (next: MergeFieldDecision) => void;
  disabled?: boolean;
}

const NULL_DISPLAY = '(empty)';

function displayValue(v: unknown): string {
  if (v === null || v === undefined) return NULL_DISPLAY;
  if (Array.isArray(v)) return v.length === 0 ? NULL_DISPLAY : v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function humanizeFieldName(field: string): string {
  // Convert snake_case → "Snake case" with proper handling of common
  // domain terms. The schema names are clear enough that lightweight
  // titling reads well to operators.
  return field
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

export function MergeFieldPicker({
  field,
  decision,
  onChange,
  disabled,
}: MergeFieldPickerProps): ReactNode {
  const radioName = `merge-field-${field.field_name}`;
  const winnerStr = displayValue(field.winner_value);
  const loserStr = displayValue(field.loser_value);
  const winnerIsEmpty = winnerStr === NULL_DISPLAY;
  const loserIsEmpty = loserStr === NULL_DISPLAY;

  // Compact "✓ matches" row when both sides agree — including when
  // both are empty (no operator action needed, but the row is still
  // tracked in field_decisions for forensic completeness).
  if (field.status === 'agree') {
    return (
      <tr className="border-b border-sage-100 last:border-0">
        <td className="px-4 py-2.5 text-sm font-medium text-ink">
          {humanizeFieldName(field.field_name)}
        </td>
        <td colSpan={3} className="px-4 py-2.5 text-sm text-sage-600">
          <Check size={14} aria-hidden="true" className="mr-1.5 inline shrink-0" />
          {winnerIsEmpty ? (
            <span className="italic text-ink-soft">both empty</span>
          ) : (
            <>matches: <span className="text-ink-soft">{winnerStr}</span></>
          )}
        </td>
      </tr>
    );
  }

  const hint =
    field.status === 'auto_inherit_non_null'
      ? winnerIsEmpty
        ? 'Only the other record has a value'
        : 'Only this record has a value'
      : 'Different values';

  return (
    <tr className="border-b border-sage-100 last:border-0">
      <td className="px-4 py-3 align-top">
        <div className="text-sm font-medium text-ink">
          {humanizeFieldName(field.field_name)}
        </div>
        <div className="mt-0.5 text-[11px] font-normal text-sage-500">
          {hint}
        </div>
      </td>

      <td className="px-4 py-3 align-top text-sm">
        <label className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-sage-50">
          <input
            type="radio"
            name={radioName}
            checked={decision.chosen === 'winner'}
            onChange={() =>
              onChange({
                winner_value: field.winner_value,
                loser_value: field.loser_value,
                chosen: 'winner',
              })
            }
            disabled={disabled}
            className="mt-0.5 accent-sage-700"
          />
          <span
            className={`break-words ${winnerIsEmpty ? 'italic text-ink-soft' : 'text-ink'}`}
          >
            {winnerStr}
          </span>
        </label>
      </td>

      <td className="px-4 py-3 align-top text-sm">
        <label className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-sage-50">
          <input
            type="radio"
            name={radioName}
            checked={decision.chosen === 'loser'}
            onChange={() =>
              onChange({
                winner_value: field.winner_value,
                loser_value: field.loser_value,
                chosen: 'loser',
              })
            }
            disabled={disabled}
            className="mt-0.5 accent-sage-700"
          />
          <span
            className={`break-words ${loserIsEmpty ? 'italic text-ink-soft' : 'text-ink'}`}
          >
            {loserStr}
          </span>
        </label>
      </td>

      <td className="px-4 py-3 align-top text-sm">
        <label className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-sage-50">
          <input
            type="radio"
            name={radioName}
            checked={decision.chosen === 'custom'}
            onChange={() =>
              onChange({
                winner_value: field.winner_value,
                loser_value: field.loser_value,
                chosen: 'custom',
                custom_value:
                  typeof decision.custom_value === 'string'
                    ? decision.custom_value
                    : '',
              })
            }
            disabled={disabled}
            className="mt-0.5 accent-sage-700"
          />
          <input
            type="text"
            value={
              typeof decision.custom_value === 'string'
                ? decision.custom_value
                : ''
            }
            onChange={(e) =>
              onChange({
                winner_value: field.winner_value,
                loser_value: field.loser_value,
                chosen: 'custom',
                custom_value: e.target.value,
              })
            }
            disabled={disabled || decision.chosen !== 'custom'}
            placeholder="Type a different value…"
            className="w-full rounded-md border border-sage-200 px-2.5 py-1 text-sm focus:border-sage-400 focus:outline-none focus:ring-1 focus:ring-sage-400 disabled:bg-sage-50/50 disabled:text-ink-soft"
          />
        </label>
      </td>
    </tr>
  );
}
